import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Eye, Layers, Search, Check, Star, ChevronDown, ChevronRight, Code, AlertTriangle, Maximize2, Minimize2, CheckCircle2 } from 'lucide-react';
import api from '../../../services/api';
import * as AssessmentBuilderAPI from '../../../services/assessmentBuilderApi';
import toast from 'react-hot-toast';
import TemplatePreview from './TemplatePreview';
import { DEVICE_DIMENSIONS, TEMPLATE_QUESTION_TYPES } from './constants';

const TemplateManagerModal = ({
  isOpen,
  onClose,
  assessmentId,
  onTemplateUpdated,
  preloadedTemplates,
  appliedDefaultTemplateIds = [],
  selectedElementData,
  applyQuestionTemplate,
  applyTemplateToAllOfType,
  screens,
  currentScreenIndex,
  deviceMode
}) => {
  const [templates, setTemplates] = useState(preloadedTemplates || { system: [], sponsor: [], user: [], assessment: [] });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name } or null
  const [deleteTypedName, setDeleteTypedName] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestionType, setSelectedQuestionType] = useState('all');
  const [settingDefault, setSettingDefault] = useState(null);

  // Derive device category from the assessment's current deviceMode
  const currentDeviceCategory = DEVICE_DIMENSIONS[deviceMode]?.category || 'desktop';
  const currentCanvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1200, height: 800 };
  const [selectedDevice, setSelectedDevice] = useState(currentDeviceCategory);

  const QUESTION_TYPE_LABELS = {
    likert: 'Ordinal (Likert)',
    rating: 'Rating',
    rating_vas: 'VAS',
    rating_nrs: 'NRS',
    text: 'Text',
    yes_no: 'Yes / No',
    nominal: 'Nominal',
    dropdown: 'Dropdown',
    date: 'Date',
    body_map: 'Body Map',
  };

  // Compute a more granular display category for templates
  // VAS and NRS are both stored as 'rating' in the backend — differentiate by name
  const getDisplayCategory = (template) => {
    if (template.question_type !== 'rating') return template.question_type;
    const name = (template.name || '').toLowerCase();
    if (name.includes('vas') || name.includes('visual analog')) return 'rating_vas';
    if (name.includes('nrs') || name.includes('numeric rating')) return 'rating_nrs';
    return 'rating';
  };

  const DEVICE_CATEGORIES = [
    { value: 'all', label: 'All Devices' },
    { value: 'phone', label: 'Mobile' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'desktop', label: 'Desktop' },
  ];

  const selectedElementType = selectedElementData?.data?.type;
  const hasSelectedQuestion = selectedElementData?.type === 'question' && !!applyQuestionTemplate;

  // Reset device filter to current assessment device when modal opens
  useEffect(() => {
    if (isOpen) setSelectedDevice(currentDeviceCategory);
  }, [isOpen]);

  // Update templates when preloaded data changes, or fetch if empty when modal opens
  useEffect(() => {
    const hasPreloaded = preloadedTemplates && (
      preloadedTemplates.system?.length > 0 ||
      preloadedTemplates.sponsor?.length > 0 ||
      preloadedTemplates.user?.length > 0 ||
      preloadedTemplates.assessment?.length > 0
    );

    if (hasPreloaded) {
      setTemplates(preloadedTemplates);
    } else if (isOpen && assessmentId) {
      fetchAllTemplates();
    }
  }, [preloadedTemplates, isOpen]);

  const fetchAllTemplates = async () => {
    setLoading(true);
    try {
      const questionTypes = TEMPLATE_QUESTION_TYPES;
      const allTemplatesMap = { system: [], sponsor: [], user: [], assessment: [] };
      const appliedIds = new Set();

      for (const qType of questionTypes) {
        try {
          const response = await api.get('/api/assessment-builder/layout-templates/available/', {
            params: { question_type: qType, assessment_id: assessmentId }
          });
          if (response.data.system_templates) allTemplatesMap.system.push(...response.data.system_templates);
          if (response.data.sponsor_templates) allTemplatesMap.sponsor.push(...response.data.sponsor_templates);
          if (response.data.user_templates) allTemplatesMap.user.push(...response.data.user_templates);
          if (response.data.assessment_templates) allTemplatesMap.assessment.push(...response.data.assessment_templates);
          if (response.data.assessment_templates_from_other_assessments) {
            allTemplatesMap.assessment.push(...response.data.assessment_templates_from_other_assessments.map(a => a.template));
          }
          if (response.data.applied_default_template_id) {
            appliedIds.add(response.data.applied_default_template_id);
          }
        } catch (err) {
          console.error(`Failed to fetch templates for ${qType}:`, err);
        }
      }

      setTemplates({
        system: Array.from(new Map(allTemplatesMap.system.map(t => [t.id, t])).values()),
        sponsor: Array.from(new Map(allTemplatesMap.sponsor.map(t => [t.id, t])).values()),
        user: Array.from(new Map(allTemplatesMap.user.map(t => [t.id, t])).values()),
        assessment: Array.from(new Map(allTemplatesMap.assessment.map(t => [t.id, t])).values()),
        appliedDefaultIds: [...appliedIds]
      });
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDelete = (template) => {
    setDeleteConfirm({ id: template.id, name: template.name });
    setDeleteTypedName('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const response = await api.delete(`/api/assessment-builder/layout-templates/${deleteConfirm.id}/`);
      if (response.data?.deleted_type === 'soft') {
        toast.success('Template removed — it was in use by other assessments so the data is preserved');
      } else {
        toast.success('Template deleted');
      }
      setDeleteConfirm(null);
      setDeleteTypedName('');
      await fetchAllTemplates();
      if (onTemplateUpdated) onTemplateUpdated();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleApply = (template) => {
    if (!applyQuestionTemplate) return;
    applyQuestionTemplate(template);
    toast.success(`Applied "${template.name}"`);
    onClose();
  };

  const handleSetDefault = async (template) => {
    if (!assessmentId) return;
    setSettingDefault(template.id);
    try {
      if (applyTemplateToAllOfType) {
        await applyTemplateToAllOfType(template);
      } else {
        await AssessmentBuilderAPI.setLayoutDefault({
          template_id: template.id,
          assessment_id: assessmentId,
          question_type: template.question_type
        });
      }
      toast.success(`"${template.name}" applied to all existing ${template.question_type || 'question'} questions and set as default`);
      if (onTemplateUpdated) onTemplateUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to set as default');
    } finally {
      setSettingDefault(null);
    }
  };

  const getTemplateDeviceCategory = (template) => template?.device_category || 'all';

  const filterTemplates = (templateList) => {
    return templateList.filter(t => {
      const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
      const displayCategory = getDisplayCategory(t);
      const matchesType = selectedQuestionType === 'all' || displayCategory === selectedQuestionType;
      const matchesDevice = selectedDevice === 'all' ||
        getTemplateDeviceCategory(t) === selectedDevice ||
        getTemplateDeviceCategory(t) === 'all';
      return matchesSearch && matchesType && matchesDevice;
    });
  };

  const allTemplates = [...templates.system, ...templates.sponsor, ...(templates.user || []), ...templates.assessment];
  const questionTypes = [...new Set(allTemplates.map(t => getDisplayCategory(t)).filter(Boolean))].sort();

  // Build question data for live preview, keyed by question_type.
  // Prefers selected element; falls back to first matching element on current screen.
  const getQuestionDataForType = (questionType) => {
    // 1. Try selected element
    if (selectedElementData?.type === 'question') {
      if (selectedElementData.data?.type === questionType) {
        const item = selectedElementData.itemData || selectedElementData.data || {};
        const text = item.text || item.question_text;
        const options = item.options || item.response_options;
        if (text || options) return { text, options };
      }
    }
    // 2. Fall back to first matching element on current screen
    const currentScreen = screens?.[currentScreenIndex];
    if (!currentScreen) return null;
    for (const el of currentScreen.elements || []) {
      if (el.type !== 'question') continue;
      const elType = el.data?.type || el.itemData?.type;
      if (elType !== questionType) continue;
      const item = el.itemData || el.data || {};
      const text = item.text || item.question_text;
      const options = item.options || item.response_options;
      if (text || options) return { text, options };
    }
    return null;
  };

  // Identify applied default template IDs — prefer prop from localAssessmentConfig (always current),
  // fall back to cache-derived IDs from the API response
  const appliedDefaultIds = new Set(
    appliedDefaultTemplateIds.length > 0
      ? appliedDefaultTemplateIds
      : (templates.appliedDefaultIds || [])
  );

  // Collect applied templates from all categories
  const appliedTemplates = allTemplates.filter(t => appliedDefaultIds.has(t.id));

  // Remaining templates per category (exclude applied ones)
  const yourTemplates = [...(templates.user || []), ...(templates.assessment || [])].filter(t => !appliedDefaultIds.has(t.id));
  const remainingSystem = (templates.system || []).filter(t => !appliedDefaultIds.has(t.id));
  const remainingSponsor = (templates.sponsor || []).filter(t => !appliedDefaultIds.has(t.id));

  const renderSection = (title, list, canDelete, { icon, accent } = {}) => {
    const filtered = filterTemplates(list);
    if (filtered.length === 0) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-2 px-1">
          {icon}
          <h3 className={`text-xs font-semibold uppercase tracking-wider ${accent || 'text-gray-500'}`}>{title}</h3>
        </div>
        <div className="space-y-2">
          {filtered.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              canDelete={canDelete}
              canApply={hasSelectedQuestion}
              isMatchingType={hasSelectedQuestion && template.question_type === selectedElementType}
              isApplied={appliedDefaultIds.has(template.id)}
              isSettingDefault={settingDefault === template.id}
              onDelete={handleRequestDelete}
              onApply={handleApply}
              onSetDefault={handleSetDefault}
              questionData={getQuestionDataForType(template.question_type)}
              canvasDims={currentCanvasDims}
            />
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Template Manager</h2>
              <p className="text-xs text-gray-500">
                {hasSelectedQuestion
                  ? `Selected: ${selectedElementType} question — apply or set defaults below`
                  : 'Select a question on the canvas to enable Apply'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
          {/* Row 1: Search + Question Type */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <select
              value={selectedQuestionType}
              onChange={(e) => setSelectedQuestionType(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Question Types</option>
              {questionTypes.map(type => (
                <option key={type} value={type}>
                  {QUESTION_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>
          </div>
          {/* Row 2: Device toggle — defaults to assessment's current device */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium mr-1">Device:</span>
            {DEVICE_CATEGORIES.filter(c => c.value !== 'all').map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSelectedDevice(value)}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                  selectedDevice === value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {label}
                {value === currentDeviceCategory && selectedDevice !== value ? ' *' : ''}
              </button>
            ))}
            <button
              onClick={() => setSelectedDevice('all')}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                selectedDevice === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-600'
              }`}
            >
              Show All
            </button>
          </div>
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Loading templates...</div>
          ) : (
            <div className="space-y-6">
              {renderSection(
                'Applied to This Assessment',
                appliedTemplates,
                false,
                {
                  icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
                  accent: 'text-emerald-600'
                }
              )}
              {renderSection('Your Templates', yourTemplates, true)}
              {renderSection('Sponsor Templates', remainingSponsor, false)}
              {renderSection('System Templates', remainingSystem, false)}
              {allTemplates.length === 0 && (
                <div className="text-center py-12 text-gray-400 text-sm">No templates found</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-80 flex items-center justify-center z-[10000]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Template</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This action cannot be undone. To confirm, type the template name below:
            </p>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2 mb-3 border border-gray-200 dark:border-gray-700">
              <span className="text-sm font-mono font-semibold text-gray-800">{deleteConfirm.name}</span>
            </div>
            <input
              type="text"
              placeholder="Type template name to confirm..."
              value={deleteTypedName}
              onChange={(e) => setDeleteTypedName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteTypedName(''); }}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteTypedName !== deleteConfirm.name}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

const TemplateCard = ({ template, canDelete, canApply, isMatchingType, isApplied, isSettingDefault, onDelete, onApply, onSetDefault, questionData, canvasDims }) => {
  const [expandedView, setExpandedView] = useState(null); // null | 'preview' | 'json'
  const [fullscreen, setFullscreen] = useState(false);

  const toggle = (view) => setExpandedView(prev => prev === view ? null : view);

  return (
    <div className={`border rounded-lg transition-colors ${
      isApplied ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-900/20' :
      isMatchingType ? 'border-primary-300 dark:border-primary-700 bg-primary-50/30 dark:bg-primary-900/20' :
      'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
    }`}>
      <div className="p-3 flex items-center gap-3">
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 text-sm">{template.name}</h4>
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
              {template.question_type}
            </span>
            {isApplied && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded font-medium">
                <CheckCircle2 className="w-3 h-3" />
                Active
              </span>
            )}
            {template.device_category && template.device_category !== 'all' && (
              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 text-xs rounded">
                {template.device_category}
              </span>
            )}
          </div>
          {template.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{template.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Apply */}
          {canApply && (
            <button
              onClick={() => onApply(template)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                isMatchingType
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={isMatchingType ? 'Apply to selected question' : 'Type mismatch — apply anyway?'}
            >
              <Check className="w-3.5 h-3.5" />
              Apply
            </button>
          )}

          {/* Use for Assessment */}
          <button
            onClick={() => onSetDefault(template)}
            disabled={isSettingDefault}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 hover:bg-yellow-50 hover:text-yellow-700 transition-colors disabled:opacity-50"
            title={`Automatically apply this template when adding new ${template.question_type || 'question'} questions to this assessment`}
          >
            <Star className="w-3.5 h-3.5" />
            {isSettingDefault ? '...' : 'Use for Assessment'}
          </button>

          {/* Preview */}
          <button
            onClick={() => toggle('preview')}
            className={`p-1.5 rounded-lg transition-colors ${expandedView === 'preview' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500'}`}
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>

          {/* JSON */}
          <button
            onClick={() => toggle('json')}
            className={`p-1.5 rounded-lg transition-colors ${expandedView === 'json' ? 'bg-gray-200 dark:bg-gray-700 text-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500'}`}
            title="View JSON config"
          >
            <Code className="w-4 h-4" />
          </button>

          {/* Delete */}
          {canDelete && (
            <button
              onClick={() => onDelete(template)}
              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete template"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {expandedView === 'preview' && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 rounded-b-lg">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setFullscreen(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              title="Fullscreen preview at actual canvas size"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Fullscreen
            </button>
          </div>
          <TemplatePreview template={template} questionData={questionData} />
        </div>
      )}

      {expandedView === 'json' && (
        <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-700">
          <pre className="mt-2 text-xs font-mono text-gray-600 bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(template.layout_config, null, 2)}
          </pre>
        </div>
      )}

      {/* Fullscreen preview overlay */}
      {fullscreen && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-80 dark:bg-opacity-90 flex items-center justify-center z-[10001]"
          onClick={() => setFullscreen(false)}
        >
          <div
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl dark:shadow-gray-900/20 p-6 max-h-[95vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{template.name}</h3>
                <p className="text-xs text-gray-500">
                  {canvasDims ? `${canvasDims.width}×${canvasDims.height}px` : 'Actual canvas size'} preview
                  {questionData ? ' — using question data' : ' — sample data'}
                </p>
              </div>
              <button
                onClick={() => setFullscreen(false)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Minimize2 className="w-3.5 h-3.5" />
                Close
              </button>
            </div>
            <TemplatePreview
              template={template}
              questionData={questionData}
              maxWidth={9999}
              canvasOverride={canvasDims ? { width: canvasDims.width, height: canvasDims.height } : undefined}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


export default TemplateManagerModal;
